import { useEffect, useState } from 'react';
import { ReloadOutlined, ApartmentOutlined } from '@ant-design/icons';
import axiosInstance from '../../utils/config';
import { Card, Typography, Row, Col, Tag, Space, Spin, Button, Divider } from '../../utils/commonImports';

const { Title, Text, Paragraph } = Typography;

const formatNumber = (value) => Number(value || 0).toLocaleString();
const labelFor = (item) => {
  if (item === null || item === undefined) return '';
  if (typeof item === 'string' || typeof item === 'number') return String(item);
  return item.name || item.label || item.type || item.path || item.file || item.qualified_name || item.handler || JSON.stringify(item);
};
const metricFor = (item) => {
  if (!item || typeof item !== 'object') return '';
  const value = item.count ?? item.node_count ?? item.edge_count ?? item.fan_in ?? item.fan_out ?? item.nodes ?? item.edges ?? '';
  return value === '' ? '' : formatNumber(value);
};

const panelStyle = {
  borderRadius: 18,
  border: '1px solid rgba(4,120,87,0.14)',
  boxShadow: '0 18px 50px rgba(1,78,56,0.08)',
};

const CodeGraphList = ({ title, items = [], empty = 'No entries reported' }) => (
  <Card size="small" title={title} style={{ ...panelStyle, height: '100%' }}>
    <Space direction="vertical" size={8} style={{ width: '100%' }}>
      {(items || []).slice(0, 10).length ? (items || []).slice(0, 10).map((item, index) => (
        <div key={`${title}-${index}`} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'baseline' }}>
          <Text style={{ maxWidth: '72%', overflowWrap: 'anywhere' }}>{labelFor(item)}</Text>
          {metricFor(item) ? <Tag color="green">{metricFor(item)}</Tag> : null}
        </div>
      )) : <Text type="secondary">{empty}</Text>}
    </Space>
  </Card>
);

const RouteList = ({ routes = [] }) => (
  <Card size="small" title="Detected routes" style={panelStyle}>
    <Space direction="vertical" size={8} style={{ width: '100%' }}>
      {(routes || []).slice(0, 18).length ? (routes || []).slice(0, 18).map((route, index) => (
        <div key={`route-${index}`} style={{ display: 'grid', gridTemplateColumns: '74px 1fr', gap: 10, alignItems: 'baseline' }}>
          <Tag color="blue">{route.method || 'ANY'}</Tag>
          <div>
            <Text strong style={{ overflowWrap: 'anywhere' }}>{route.path || route.name}</Text>
            {route.handler ? <Paragraph type="secondary" style={{ margin: 0, fontSize: 12, overflowWrap: 'anywhere' }}>{route.handler}</Paragraph> : null}
          </div>
        </div>
      )) : <Text type="secondary">No HTTP routes detected in graph.</Text>}
    </Space>
  </Card>
);

const ProjectGraphCard = ({ project }) => {
  const nodes = Number(project?.totals?.nodes || 0);
  const edges = Number(project?.totals?.edges || 0);
  const max = Math.max(nodes, edges, 1);
  const bars = [
    { label: 'Nodes', value: nodes, color: '#047857' },
    { label: 'Edges', value: edges, color: '#1677ff' },
  ];

  return (
    <Card style={{ ...panelStyle, marginBottom: 18 }}>
      <Row gutter={[18, 18]} align="top">
        <Col xs={24} lg={15}>
          <Space direction="vertical" size={8} style={{ width: '100%' }}>
            <Text type="secondary" style={{ textTransform: 'uppercase', letterSpacing: 1 }}>{project.kind || 'Indexed project'}</Text>
            <Title level={3} style={{ margin: 0 }}>{project.label || project.project}</Title>
            <Paragraph type="secondary" style={{ margin: 0 }}>{project.description || project.rootPath}</Paragraph>
            <Space wrap>
              <Tag color="green">{project.project || 'not indexed'}</Tag>
              <Tag>{project.rootPath}</Tag>
            </Space>
            {project.error ? <Tag color="red">{project.error}</Tag> : null}
          </Space>
        </Col>
        <Col xs={24} lg={9}>
          <Row gutter={[10, 10]}>
            <Col span={8}><Card size="small" style={{ textAlign: 'center' }}><Title level={4} style={{ margin: 0 }}>{formatNumber(nodes)}</Title><Text type="secondary">Nodes</Text></Card></Col>
            <Col span={8}><Card size="small" style={{ textAlign: 'center' }}><Title level={4} style={{ margin: 0 }}>{formatNumber(edges)}</Title><Text type="secondary">Edges</Text></Card></Col>
            <Col span={8}><Card size="small" style={{ textAlign: 'center' }}><Title level={4} style={{ margin: 0 }}>{formatNumber(project?.totals?.sizeBytes || 0)}</Title><Text type="secondary">Bytes</Text></Card></Col>
          </Row>
        </Col>
      </Row>

      <div style={{ height: 118, margin: '22px 0', borderRadius: 18, border: '1px solid rgba(4,120,87,0.12)', padding: 14, display: 'flex', alignItems: 'end', gap: 14, background: 'linear-gradient(180deg, rgba(4,120,87,0.08), rgba(255,255,255,0.6))' }}>
        {bars.map((bar) => <div key={bar.label} title={`${bar.label}: ${formatNumber(bar.value)}`} style={{ width: 90, height: `${Math.max(14, (bar.value / max) * 100)}%`, borderRadius: '12px 12px 4px 4px', background: bar.color }} />)}
        <div style={{ flex: 1 }} />
      </div>

      <Row gutter={[12, 12]}>
        <Col xs={24} md={12} xl={6}><CodeGraphList title="Node labels" items={project.nodeLabels} /></Col>
        <Col xs={24} md={12} xl={6}><CodeGraphList title="Edge types" items={project.edgeTypes} /></Col>
        <Col xs={24} md={12} xl={6}><CodeGraphList title="Packages / clusters" items={project.packages || project.clusters} /></Col>
        <Col xs={24} md={12} xl={6}><CodeGraphList title="Entry points" items={project.entryPoints} /></Col>
        <Col xs={24}><RouteList routes={project.routes} /></Col>
      </Row>
    </Card>
  );
};

const CodeGraph = () => {
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadGraph = async (force = false) => {
    setLoading(true);
    setError('');
    try {
      const response = await axiosInstance.get(`/api/v1/admin/code-graph${force ? '?refresh=1' : ''}`);
      setPayload(response.data.data);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Unable to load code graph');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadGraph(false); }, []);

  const totals = payload?.totals || { nodes: 0, edges: 0 };

  return (
    <div style={{ padding: 24 }}>
      <Card style={{ ...panelStyle, marginBottom: 18, background: 'linear-gradient(135deg, rgba(4,120,87,0.10), rgba(22,119,255,0.07))' }}>
        <Row gutter={[18, 18]} justify="space-between" align="middle">
          <Col xs={24} lg={16}>
            <Space direction="vertical" size={8}>
              <Tag color="green" icon={<ApartmentOutlined />}>Codebase Memory</Tag>
              <Title level={2} style={{ margin: 0 }}>Code Graph</Title>
              <Paragraph type="secondary" style={{ margin: 0, maxWidth: 760 }}>
                Admin-only architecture map for the NeoTerra website/backend. The backend calls the global codebase-memory MCP index and returns a redacted summary only.
              </Paragraph>
            </Space>
          </Col>
          <Col xs={24} lg={8} style={{ textAlign: 'right' }}>
            <Button type="primary" icon={<ReloadOutlined />} loading={loading} onClick={() => loadGraph(true)}>Refresh graph</Button>
          </Col>
        </Row>
      </Card>

      {error ? <Card style={{ ...panelStyle, marginBottom: 18 }}><Text type="danger">{error}</Text></Card> : null}

      <Row gutter={[14, 14]} style={{ marginBottom: 18 }}>
        <Col xs={24} md={8}><Card style={panelStyle}><Title level={3} style={{ margin: 0 }}>{formatNumber(totals.nodes)}</Title><Text type="secondary">Total nodes</Text></Card></Col>
        <Col xs={24} md={8}><Card style={panelStyle}><Title level={3} style={{ margin: 0 }}>{formatNumber(totals.edges)}</Title><Text type="secondary">Total edges</Text></Card></Col>
        <Col xs={24} md={8}><Card style={panelStyle}><Title level={3} style={{ margin: 0 }}>{formatNumber(payload?.projects?.length || 0)}</Title><Text type="secondary">Indexed project bindings</Text></Card></Col>
      </Row>

      <Divider orientation="left">Architecture summaries</Divider>
      {loading && !payload ? <div style={{ display: 'grid', placeItems: 'center', minHeight: 220 }}><Spin size="large" /></div> : null}
      {(payload?.projects || []).map((project, index) => <ProjectGraphCard project={project} key={project.project || project.rootPath || index} />)}
    </div>
  );
};

export default CodeGraph;
